import {
	type Middleware,
	type MiddlewareAPI,
	type Dispatch,
	type AnyAction,
	isRejectedWithValue
} from '@reduxjs/toolkit'
import { createElement } from 'react'
import { toast } from 'react-toastify'
import { setIsAuthenticated } from '@/entities/session'
import { setCurrentUser } from '@/entities/user'
import { FreePlanLimitToast } from '@/components/free-plan'
import { isFreePlanClientLimitError, isNoSubscriptionError } from '@/lib/access'
import { baseApi, printError, TAGS } from '@/shared/api'
import { addDotAtEnd } from '@/shared/lib'

const getIsUnauthorized = (action: any): boolean => {
	const isBaseQuery401 = action.meta.baseQueryMeta?.response?.status === 401
	const isCurrentUserThunkRejected = action.type === 'user/currentUser/rejected'
	const isLoginThunkRejected = action.type === 'authentication/login/rejected'
	return isBaseQuery401 || isCurrentUserThunkRejected || isLoginThunkRejected
}

const handleUnauthorizedUser = (
	action: any,
	next: Dispatch<AnyAction>,
	dispatch: Dispatch<AnyAction>
) => {
	dispatch(setIsAuthenticated(false))
	dispatch(setCurrentUser(null))
	return next(action)
}

const handleUserProfilePhotoError = (
	action: any,
	next: Dispatch<AnyAction>,
	dispatch: Dispatch<AnyAction>
) => {
	toast.error('An error occurred while updating your profile photo.')
	return next(action)
}

export const errorMiddleware: Middleware =
	({ dispatch }: MiddlewareAPI) =>
	(next) =>
	(action) => {
		if (isRejectedWithValue(action)) {
			// Access ended mid-session (e.g. the free plan's 90 days ran out). The
			// session itself is fine, so refetch the user instead of logging out —
			// the access guards then show the paywall.
			if (
				action.meta.baseQueryMeta?.response?.status === 401 &&
				isNoSubscriptionError(action.payload)
			) {
				dispatch(baseApi.util.invalidateTags([TAGS.ME]))
				return next(action)
			}

			// The free plan's one client is taken: say so with a way to upgrade, and
			// refetch the user so every add-client entry point switches over.
			if (isFreePlanClientLimitError(action.payload)) {
				dispatch(baseApi.util.invalidateTags([TAGS.ME]))
				toast.error(
					createElement(FreePlanLimitToast, {
						message: addDotAtEnd(String(action.payload))
					})
				)
				return next(action)
			}

			const isUnauthorized = getIsUnauthorized(action)

			if (isUnauthorized) return handleUnauthorizedUser(action, next, dispatch)
			if (action.meta.arg?.endpointName === 'editUserProfilePhoto')
				return handleUserProfilePhotoError(action, next, dispatch)

			// ignore 404 errors for chat history
			if (
				action.meta.arg.endpointName === 'getChatHistoryMessages' &&
				action.meta.baseQueryMeta?.response?.status === 404
			) {
				return next(action)
			}

			// ignore 404 errors for tax plan notes
			if (
				action.meta.arg.endpointName === 'getNotes' &&
				action.meta.baseQueryMeta?.response?.status === 404
			) {
				return next(action)
			}

			// ignore 404 errors for tax plan notes
			if (
				action.meta.arg.endpointName === 'initXeroAuth' &&
				action.meta.baseQueryMeta?.response?.status === 404
			) {
				return next(action)
			}

			// ignore 404 for AI report — the page auto-generates when not found
			if (
				action.meta.arg.endpointName === 'getAiReport' &&
				action.meta.baseQueryMeta?.response?.status === 404
			) {
				return next(action)
			}

			// Show a specific message for legacy plans — the alert dialog handles the action
			if (
				action.meta.arg?.endpointName === 'getPlanDetailsInfo' &&
				action.payload?.errorCode === 'LEGACY_PLAN'
			) {
				toast.error('This plan is no longer supported.')
				return next(action)
			}

			// Prevent toast for verifyInviteToken endpoint
			if (action.meta.arg?.endpointName === 'verifyInviteToken') return

			// applyAiStrategy handles its own user-facing error messages in the catch block
			if (action.meta.arg?.endpointName === 'applyAiStrategy')
				return next(action)

			toast.error(addDotAtEnd(printError(action.payload)))
		}

		return next(action)
	}
